import React, { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useAuthCurator, useRole } from "@src/hooks/useAuth";
import { useMyCurators } from "@src/hooks/useMyCurators";
import { useCuratorMedia } from "@src/queries/curatorQueries";
import { useCuratorShowPromosQuery } from "@src/queries/showQueries";
import { ICuratorMedia } from "@apis/curatorApis";
import { buildMediaUrl } from "@src/utils/mediaUrl";
import { saveAs } from "file-saver";
import { Loader2 } from "lucide-react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { useNotification } from "@src/context/NotificationContext";
import useModal from "@src/hooks/useModal";

import WrapperContainer from "@components/WrapperContainer";
import ContentContainer from "@src/components/ContentContainer";
import PageTopSection from "@src/components/PageTopSection";
import PageTitle from "@src/components/PageTitle";
import AppSelectDropdown from "@src/components/AppSelectDropdown";
import CuratorDropdownOption from "@src/components/CuratorDropdownOption";
import { AppSelectDropdownOption } from "@src/components/AppSelectDropdown/AppSelectDropdown";
import { IGuestCuratorRecord } from "@src/types/curator";
import { theme } from "@src/theme";
import InstagramStoryModal, { StoryCardActions } from "./InstagramStoryModal";
import { fetchImageBlob } from "./instagramStory/loadImage";

import {
  PlaylistOverviewHeader,
  PlaylistOverviewHeaderLeft,
  PlaylistOverviewHeaderRight,
  ButtonSection,
  Subtitle,
  SelectContainer,
  ContentWrapper,
  Section,
  SectionTitle,
  Grid,
  Card,
  ImageWrapper,
  Image,
  CardContent,
  CardTitle,
  CardMeta,
  MetaBadge,
  CardActions,
  LoadingContainer,
  EmptyState,
  EmptyTitle,
} from "./SocialMediaPromo.styles";

export const SocialMediaPromo: React.FC = () => {
  const curator = useAuthCurator();
  const role = useRole();
  const { addNotification } = useNotification();

  const [searchQuery, setSearchQuery] = useState<string>("");

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);

  // Initialize react-hook-form to provide control context to AppSelectDropdown
  const { control, watch, setValue } = useForm({
    defaultValues: {
      curatorSelect: "",
    },
  });

  // Only admins and managers browse other curators. A curator only ever sees their
  // own media, so the picker is theirs alone — and so is the request behind it.
  const showSelector = role === "admin" || role === "manager";

  // Admins and managers start on an empty gallery and pick from the dropdown; a
  // curator is pinned to their own id, with no render in between where the page
  // has nobody selected.
  const selectedCuratorId = showSelector
    ? watch("curatorSelect")
    : curator?.documentId || "";
  const [loadedCuratorId, setLoadedCuratorId] = useState<string>("");

  // Fetch all active curators if logged in user is admin/manager
  const { myCurators, fetchingMyCurators } = useMyCurators(
    curator?.documentId || "",
    searchQuery,
    role,
    { enabled: showSelector }
  );

  // Fetch media for the selected curator
  const {
    data: mediaResponse,
    isLoading: isLoadingMedia,
    isFetching: isFetchingMedia,
  } = useCuratorMedia(selectedCuratorId, {
    enabled: !!selectedCuratorId,
  });

  // The curator's shows back the Instagram Story prefill. Loaded alongside the
  // gallery so the modal opens already filled instead of flashing placeholders.
  const { data: curatorShows } = useCuratorShowPromosQuery(selectedCuratorId, {
    enabled: !!selectedCuratorId,
  });

  // Track when a query fetch has completed for the selected curator
  useEffect(() => {
    if (!isFetchingMedia && selectedCuratorId) {
      setLoadedCuratorId(selectedCuratorId);
    }
  }, [isFetchingMedia, selectedCuratorId]);

  // Track downloading states per image ID
  const [downloadingMap, setDownloadingMap] = useState<{
    [id: number]: boolean;
  }>({});
  const [storyMedia, setStoryMedia] = useState<ICuratorMedia | null>(null);
  const {
    isOpen: isStoryModalOpen,
    openModal: openStoryModal,
    closeModal: closeStoryModal,
  } = useModal();

  const media = mediaResponse?.data?.data || [];

  const selectedCuratorName = useMemo(() => {
    if (role === "admin" || role === "manager") {
      const selected = myCurators.find(
        (item) => item.documentId === selectedCuratorId
      );
      return selected?.name || "";
    }
    return curator?.name || "";
  }, [curator?.name, myCurators, role, selectedCuratorId]);

  const getFormatLabel = (mimeType?: string) => {
    if (!mimeType) return "IMG";
    const parts = mimeType.split("/");
    if (parts.length > 1) {
      const ext = parts[1].toUpperCase();
      return ext === "JPEG" ? "JPG" : ext;
    }
    return "IMG";
  };

  const formatBytes = (bytes?: number, decimals = 2) => {
    if (!bytes) return "N/A";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    // Clamp to the last unit so anything past it still reads as TB, not "undefined".
    const i = Math.min(
      Math.floor(Math.log(bytes) / Math.log(k)),
      sizes.length - 1
    );
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const handleDownload = async (img: ICuratorMedia) => {
    if (!img.url) return;

    const downloadUrl = buildMediaUrl(img.url);
    const fileName = img.name || "download.jpg";

    try {
      setDownloadingMap((prev) => ({ ...prev, [img.id]: true }));
      // Must go through fetchImageBlob: a plain fetch() reuses the non-CORS
      // response the thumbnail <img> already cached, throws, and drops us into
      // the fallback below — which opens the image in a new tab instead of
      // downloading it.
      const blob = await fetchImageBlob(downloadUrl);
      saveAs(blob, fileName);
    } catch {
      // Fallback: browsers ignore the `download` attribute on a cross-origin
      // link, so open the image in a new tab and let the user save it from
      // there rather than navigating the dashboard away.
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
      // The new tab takes focus, so this warning sits on a background tab: give
      // it long enough to still be there when the user comes back, and say what
      // to do rather than just what failed.
      addNotification(
        'Your browser blocked the download. We opened the image in a new tab — right-click it and choose "Save image as…".',
        "warning",
        10000
      );
    } finally {
      setDownloadingMap((prev) => ({ ...prev, [img.id]: false }));
    }
  };

  const handleOpenStory = (img: ICuratorMedia): void => {
    setStoryMedia(img);
    openStoryModal();
  };

  const handleCloseStory = (): void => {
    closeStoryModal();
    setStoryMedia(null);
  };

  const renderImageCard = (img: ICuratorMedia, index: number) => {
    const isDownloading = !!downloadingMap[img.id];
    return (
      <Card key={img.id}>
        <ImageWrapper
          onClick={() => {
            setLightboxIndex(index);
            setLightboxOpen(true);
          }}
          style={{ cursor: "pointer" }}
        >
          <Image src={buildMediaUrl(img.url)} alt={img.name} loading="lazy" />
        </ImageWrapper>
        <CardContent>
          <CardTitle title={img.name}>
            {img.name ? img.name.replace(/\.[^/.]+$/, "") : "Promo Photo"}
          </CardTitle>
          <CardMeta>
            {img.width && img.height && (
              <MetaBadge>
                {img.width}x{img.height}
              </MetaBadge>
            )}
            <MetaBadge>{getFormatLabel(img.mime)}</MetaBadge>
            <MetaBadge>{formatBytes(img.size)}</MetaBadge>
          </CardMeta>
          <CardActions>
            <StoryCardActions
              onOpenStory={() => handleOpenStory(img)}
              onDownload={() => handleDownload(img)}
              isDownloading={isDownloading}
            />
          </CardActions>
        </CardContent>
      </Card>
    );
  };

  // Show loading indicator when the query is actively loading/fetching,
  // or when the currently selected curator ID does not match the loaded dataset ID yet.
  const showLoading =
    isLoadingMedia || isFetchingMedia || selectedCuratorId !== loadedCuratorId;

  return (
    <WrapperContainer>
      <PageTopSection allowOverflow>
        <PlaylistOverviewHeader>
          <PlaylistOverviewHeaderLeft>
            <ButtonSection>
              <PageTitle>Social Media Promo</PageTitle>
              <Subtitle>
                View and download yellow (ROVRized) photos, playlist covers, and
                Instagram Story templates.
              </Subtitle>
            </ButtonSection>
          </PlaylistOverviewHeaderLeft>

          <PlaylistOverviewHeaderRight style={{ zIndex: 100 }}>
            {showSelector && (
              <SelectContainer>
                <AppSelectDropdown
                  overrideBorderBottomColor={theme.colors.gray150}
                  searching={fetchingMyCurators}
                  setSearchText={setSearchQuery}
                  searchText={searchQuery}
                  options={myCurators}
                  labelAccessor="name"
                  valueAccessor="documentId"
                  label={
                    role === "admin" || role === "manager" ? "Curator" : "Guest"
                  }
                  errors={{}}
                  name="curatorSelect"
                  control={control}
                  value={selectedCuratorId}
                  onChange={(val) => setValue("curatorSelect", val)}
                  resetValue={() => setValue("curatorSelect", "")}
                  placeholder={
                    role === "admin" || role === "manager"
                      ? "Select curator"
                      : "Select guest"
                  }
                  selectContainerStyle={{ minHeight: "auto", marginBottom: 0 }}
                  renderItem={(option: AppSelectDropdownOption) => {
                    const item = option as IGuestCuratorRecord;
                    return (
                      <CuratorDropdownOption
                        name={item.name}
                        imageUrl={
                          item?.webCuratorCatalog?.formats?.thumbnail?.url
                        }
                      />
                    );
                  }}
                />
              </SelectContainer>
            )}
          </PlaylistOverviewHeaderRight>
        </PlaylistOverviewHeader>
      </PageTopSection>

      <ContentContainer allowScroll={true}>
        <ContentWrapper>
          {!selectedCuratorId ? (
            <EmptyState>
              {/* A curator has no dropdown to pick from, so the only way they land
                  here is an unresolved curator profile. */}
              <EmptyTitle>
                {showSelector ? "Select a Curator" : "No Media Found"}
              </EmptyTitle>
              <Subtitle>
                {showSelector
                  ? "Please choose a curator from the dropdown to view and download their promotional assets."
                  : "We could not load your curator profile, so there is nothing to show yet."}
              </Subtitle>
            </EmptyState>
          ) : showLoading ? (
            <LoadingContainer>
              <Loader2
                size={40}
                style={{
                  color: "#FFF057",
                  animation: "spin 1s linear infinite",
                }}
              />
              <span>Fetching media assets...</span>
            </LoadingContainer>
          ) : media.length === 0 ? (
            <EmptyState>
              <EmptyTitle>No Media Found</EmptyTitle>
              <Subtitle>
                This curator has not uploaded any catalog photos or playlists
                yet.
              </Subtitle>
            </EmptyState>
          ) : (
            <Section>
              <SectionTitle>Promo Photos & Playlist Covers</SectionTitle>
              <Grid>{media.map((img, idx) => renderImageCard(img, idx))}</Grid>
            </Section>
          )}
        </ContentWrapper>
      </ContentContainer>

      <InstagramStoryModal
        isOpen={isStoryModalOpen}
        onClose={handleCloseStory}
        media={storyMedia}
        curatorName={selectedCuratorName}
        shows={curatorShows}
      />

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={media.map((img) => ({
          src: buildMediaUrl(img.url),
          alt: img.name,
        }))}
      />

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </WrapperContainer>
  );
};

export default SocialMediaPromo;
